import { useCallback, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type CollisionDetection,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { DayColumn } from './DayColumn'
import { TodoItem } from './TodoItem'
import { useTodos, useUpdateTodo } from '../api/hooks'
import { useUIStore } from '../store/ui'
import { formatDate, todayStr } from '../lib/parse'
import { between, atEnd, atStart } from '../lib/fracidx'
import { sortTodos, computeSnappedPosition } from '../lib/todoSort'
import { Button } from './primitives/Button'
import type { Todo } from '../api/types'

// Describes where the dragged item should appear during a drag (before the
// item is committed to the server). Used to drive cross-column landing preview.
type DragOverride = {
  id: string         // dragged todo id
  date: string       // target column dateStr
  beforeId: string | null  // id of the todo it should appear ABOVE; null = append to end
} | null

// Compute a synthetic position for a todo being temporarily placed in a column
// during a drag. col is sorted and excludes the dragged item.
// beforeId === null → append after last; otherwise insert before the named todo.
function computeOverridePosition(col: Todo[], beforeId: string | null): number {
  if (beforeId === null) {
    return col.length === 0 ? 1 : atEnd(col[col.length - 1].position)
  }
  const i = col.findIndex((t) => t.id === beforeId)
  if (i === -1) {
    // beforeId not found — append
    return col.length === 0 ? 1 : atEnd(col[col.length - 1].position)
  }
  if (i === 0) {
    return atStart(col[0].position)
  }
  return between(col[i - 1].position, col[i].position)
}

// A2: Custom collision detection that prefers todo droppables over the column
// droppable. When the pointer is only inside the column droppable (e.g. cursor
// is in the L/R padding bands where todo rects don't extend to), we find the
// Y-closest todo droppable within that column's X range and return it instead.
// This prevents cursor-in-padding-band from snapping to column bottom.
// Only when the cursor is genuinely below the last todo do we keep the column
// droppable so "drop at end" remains reachable.
const collisionDetectionStrategy: CollisionDetection = (args) => {
  const pointer = pointerWithin(args)
  if (pointer.length === 0) {
    const intersecting = rectIntersection(args)
    if (intersecting.length > 0) return intersecting
    return closestCenter(args)
  }

  const overTodos = pointer.filter(c => !String(c.id).startsWith('day-'))
  if (overTodos.length > 0) return overTodos

  // Only the column droppable is under the pointer. Find the Y-closest todo
  // droppable inside this column so cursor in L/R padding bands or gap above
  // first todo still snaps to a real slot.
  const columnCollision = pointer[0]
  const columnRect = args.droppableRects.get(columnCollision.id)
  const cursor = args.pointerCoordinates
  if (!columnRect || !cursor) return pointer

  const todosInCol: Array<{ id: string | number; rect: { top: number; bottom: number; height: number; left: number; right: number; width: number } }> = []
  for (const c of args.droppableContainers) {
    const id = c.id
    if (String(id).startsWith('day-')) continue
    const rect = args.droppableRects.get(id)
    if (!rect) continue
    const midX = rect.left + rect.width / 2
    if (midX < columnRect.left || midX > columnRect.right) continue
    todosInCol.push({ id, rect })
  }

  if (todosInCol.length === 0) return pointer

  todosInCol.sort((a, b) => a.rect.top - b.rect.top)
  const lastBottom = todosInCol[todosInCol.length - 1].rect.bottom

  // Cursor below last todo → keep column droppable so user can still drop-at-end.
  if (cursor.y > lastBottom) return pointer

  let best = todosInCol[0]
  let bestDist = Math.abs((best.rect.top + best.rect.height / 2) - cursor.y)
  for (let i = 1; i < todosInCol.length; i++) {
    const r = todosInCol[i]
    const d = Math.abs((r.rect.top + r.rect.height / 2) - cursor.y)
    if (d < bestDist) {
      best = r
      bestDist = d
    }
  }
  return [{ id: best.id }]
}

// Single source of truth for available day-count options.
// Add or remove values here to update the footer buttons automatically.
const DAY_COUNT_OPTIONS = [1, 3, 5, 7] as const

// Build an array of `count` consecutive Date objects starting from `startDate`.
function buildWeekDates(startDate: Date, count: number): Date[] {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(startDate)
    d.setDate(d.getDate() + i)
    return d
  })
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function WeeklyGrid() {
  const focusedDate = useUIStore((s) => s.focusedDate)
  const setFocusedDate = useUIStore((s) => s.setFocusedDate)
  const weekDayCount = useUIStore((s) => s.weekDayCount)
  const setWeekDayCount = useUIStore((s) => s.setWeekDayCount)
  const smartStatusSort = useUIStore((s) => s.smartStatusSort)

  // focusedDate is treated as the first day shown, not the week's Sunday.
  const weekDates = useMemo(
    () => buildWeekDates(new Date(focusedDate), weekDayCount),
    [focusedDate, weekDayCount]
  )

  // Padded range: one extra day on each side of the visible window. These
  // off-screen columns are what slide into view during a horizontal drag, so
  // the user sees the real adjacent days rather than blank space.
  const paddedDates = useMemo(() => {
    const start = new Date(focusedDate)
    start.setDate(start.getDate() - 1)
    return buildWeekDates(start, weekDayCount + 2)
  }, [focusedDate, weekDayCount])

  const fetchStart = formatDate(paddedDates[0])
  const fetchEnd = formatDate(paddedDates[paddedDates.length - 1])

  const { data: todos = [], isLoading } = useTodos({ from: fetchStart, to: fetchEnd })
  const updateTodo = useUpdateTodo()

  // Track the id of the item currently being dragged so we can render its
  // floating overlay copy with DragOverlay.
  const [activeId, setActiveId] = useState<string | null>(null)
  const activeTodo = useMemo(
    () => (activeId ? todos.find((t) => t.id === activeId) ?? null : null),
    [activeId, todos]
  )

  // Transient override: while a drag is in progress this encodes where the
  // dragged item should appear in the UI (potentially in a different column
  // than it lives on the server). Cleared on dragEnd and dragCancel.
  const [dragOverride, setDragOverride] = useState<DragOverride>(null)

  // Derive column contents from server todos + any active drag override.
  // The override splices the dragged item into the destination column so that
  // dnd-kit's SortableContext in that column sees it and can animate a slot.
  // Critical invariant: the dragged item appears in exactly one column at
  // a time — the filter step removes it from the server position before
  // re-inserting it at the override position.
  const groupedTodos = useMemo(() => {
    let working = todos
    if (dragOverride) {
      const dragged = todos.find((t) => t.id === dragOverride.id)
      if (dragged) {
        // Remove from wherever the server says it is
        working = working.filter((t) => t.id !== dragOverride.id)
        // Build the target column's sorted list (excluding the dragged item)
        const targetCol = working
          .filter((t) => t.date === dragOverride.date)
          .sort((a, b) => a.position - b.position)
        const syntheticPos = computeOverridePosition(targetCol, dragOverride.beforeId)
        working = [
          ...working,
          { ...dragged, date: dragOverride.date, position: syntheticPos },
        ]
      }
    }
    // Group by date, sort within each group
    const out: Record<string, Todo[]> = {}
    for (const t of working) {
      if (!t.date) continue
      if (!out[t.date]) out[t.date] = []
      out[t.date].push(t)
    }
    for (const k of Object.keys(out)) {
      out[k] = sortTodos(out[k], smartStatusSort)
    }
    return out
  }, [todos, dragOverride, smartStatusSort])

  const getTodosForDate = useCallback(
    (dateStr: string): Todo[] => groupedTodos[dateStr] ?? [],
    [groupedTodos]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }, [])

  // Called on every pointer-move during a drag. Updates the override so the
  // destination column immediately shows a landing slot. Uses compare-then-set
  // to return the previous state object unchanged when the slot hasn't moved,
  // preventing spurious re-renders during pointer movement.
  //
  // Direction (insert before vs after) is determined by PRE-DRAG indices in the
  // target column, NOT by animated rect midpoints. Rect midpoints oscillate
  // mid-animation (verticalListSortingStrategy moves items to make room), causing
  // the old comparison to flip back and forth → vibration + off-by-one on release.
  // Index comparison is stable across renders.
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event
    if (!over) {
      setDragOverride(null)
      return
    }
    const draggedId = active.id as string
    if (over.id === draggedId) return  // hovering self — leave override unchanged

    const dragged = todos.find(t => t.id === draggedId)
    if (!dragged) return

    const overData = over.data?.current as { date?: string; type?: string } | undefined

    // Hovering over a column droppable — append to that column.
    if (overData?.type === 'day' && overData.date) {
      setDragOverride(prev =>
        prev?.id === draggedId && prev.date === overData.date && prev.beforeId === null
          ? prev
          : { id: draggedId, date: overData.date!, beforeId: null }
      )
      return
    }

    // Hovering over a todo droppable.
    const overTodo = todos.find(t => t.id === over.id)
    if (!overTodo) return
    const targetDate = overTodo.date ?? dragged.date
    if (!targetDate) return

    // Direction is determined by PRE-DRAG indices in the target column.
    // Same column AND active sat above over → moving DOWN onto over → insert AFTER.
    // Otherwise insert BEFORE.
    const sameColumn = dragged.date === targetDate
    let beforeId: string | null = overTodo.id  // default: BEFORE over

    if (sameColumn) {
      const colSorted = todos
        .filter(t => t.date === targetDate)
        .sort((a, b) => a.position - b.position)
      const activeIdx = colSorted.findIndex(t => t.id === draggedId)
      const overIdx   = colSorted.findIndex(t => t.id === overTodo.id)
      if (activeIdx !== -1 && overIdx !== -1 && activeIdx < overIdx) {
        // Moving DOWN — insert AFTER over → beforeId = id of next, or null if last.
        const next = colSorted[overIdx + 1]
        beforeId = next?.id ?? null
      }
    }

    setDragOverride(prev =>
      prev?.id === draggedId && prev.date === targetDate && prev.beforeId === beforeId
        ? prev
        : { id: draggedId, date: targetDate, beforeId }
    )
  }, [todos])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      // No valid drop target or no override recorded — bail without mutation.
      if (!over || !dragOverride) {
        setDragOverride(null)
        setActiveId(null)
        return
      }

      const draggedTodoId = active.id as string
      const targetDate = dragOverride.date

      // Build the target column WITHOUT the dragged item so position math is clean.
      // Use the raw server todos (not groupedTodos) so we don't rely on the
      // synthetic position that was only used for preview rendering.
      let finalPosition: number
      if (smartStatusSort) {
        const draggedTodo = todos.find((t) => t.id === draggedTodoId)
        if (!draggedTodo) {
          setDragOverride(null)
          setActiveId(null)
          return
        }
        const targetCol = sortTodos(
          todos.filter((t) => t.date === targetDate && t.id !== draggedTodoId),
          true,
        )
        finalPosition = computeSnappedPosition(targetCol, draggedTodo.status, dragOverride.beforeId)
      } else {
        const targetCol = todos
          .filter((t) => t.date === targetDate && t.id !== draggedTodoId)
          .sort((a, b) => a.position - b.position)
        finalPosition = computeOverridePosition(targetCol, dragOverride.beforeId)
      }

      // Fire the optimistic mutation FIRST so the cache update lands before
      // DragOverlay tears down. This prevents the overlay from snapping back
      // to the source rect while the cache still shows the item there.
      updateTodo.mutate({ id: draggedTodoId, date: targetDate, position: finalPosition })

      setDragOverride(null)
      setActiveId(null)
    },
    [todos, dragOverride, smartStatusSort, updateTodo]
  )

  const handleDragCancel = useCallback(() => {
    setDragOverride(null)
    setActiveId(null)
  }, [])

  const today = todayStr()

  const goToPrevWeek = useCallback(() => {
    const d = new Date(focusedDate)
    d.setDate(d.getDate() - weekDayCount)
    setFocusedDate(formatDate(d))
  }, [focusedDate, weekDayCount, setFocusedDate])

  const goToNextWeek = useCallback(() => {
    const d = new Date(focusedDate)
    d.setDate(d.getDate() + weekDayCount)
    setFocusedDate(formatDate(d))
  }, [focusedDate, weekDayCount, setFocusedDate])

  const goToToday = useCallback(() => {
    setFocusedDate(today)
  }, [today, setFocusedDate])

  // ── Horizontal "grab the header and slide the window" interaction ──────────
  // Drag the day-header strip left/right to shift the visible date window by
  // whole days. Dragging right reveals earlier dates (e.g. Tue→Mon as the first
  // column); dragging left reveals later ones. The window snaps to whole-day
  // boundaries on release.
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ startX: number; colWidth: number } | null>(null)
  const [dragDx, setDragDx] = useState(0)
  const [dragging, setDragging] = useState(false)

  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    // Only start the window-shift drag from a day header — never from the
    // todo list, inputs, or empty column body (those belong to todo DnD).
    if (!(e.target as HTMLElement).closest('.day-col-header')) return
    const vp = viewportRef.current
    if (!vp) return
    dragRef.current = { startX: e.clientX, colWidth: vp.clientWidth / weekDayCount }
    setDragging(true)
    setDragDx(0)
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [weekDayCount])

  const onHeaderPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return
    setDragDx(e.clientX - dragRef.current.startX)
  }, [])

  const onHeaderPointerUp = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    dragRef.current = null
    setDragging(false)
    setDragDx(0)
    // Drag right (positive dx) → window moves back in time → subtract days.
    const shiftDays = Math.round((e.clientX - d.startX) / d.colWidth)
    if (shiftDays !== 0) {
      const next = new Date(focusedDate)
      next.setDate(next.getDate() - shiftDays)
      setFocusedDate(formatDate(next))
    }
  }, [focusedDate, setFocusedDate])

  const onHeaderPointerCancel = useCallback(() => {
    dragRef.current = null
    setDragging(false)
    setDragDx(0)
  }, [])

  // Check if we're viewing a range that includes today
  const isCurrentWeek = weekDates.some((d) => formatDate(d) === today)

  // Format week range label using the dynamic last index
  const weekLabel = (() => {
    const start = weekDates[0]
    const end = weekDates[weekDates.length - 1]
    if (start.getMonth() === end.getMonth()) {
      return `${MONTHS[start.getMonth()]} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`
    }
    return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Week navigation header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 24px',
          background: 'var(--color-paper)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          className="week-nav-btn"
          onClick={goToPrevWeek}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-ink-muted)',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
          title="Previous period"
          aria-label="Previous period"
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
        </button>

        <span
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '0.9375rem',
            color: 'var(--color-ink)',
            flex: 1,
            textAlign: 'center',
          }}
        >
          {weekLabel}
        </span>

        <button
          className="week-nav-btn"
          onClick={goToNextWeek}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-ink-muted)',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
          title="Next period"
          aria-label="Next period"
        >
          <ArrowRight size={16} strokeWidth={1.5} />
        </button>

        {!isCurrentWeek && (
          <Button variant="bracketed" onClick={goToToday} style={{ marginLeft: '12px' }}>
            today
          </Button>
        )}
      </div>

      {/* Day columns */}
      {isLoading ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              color: 'var(--color-ink-faint)',
            }}
          >
            loading…
          </span>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetectionStrategy}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {/* Viewport clips the padded row; the row is wider than the viewport
              and offset by one column so the leading/trailing padding days sit
              just off-screen, ready to slide in during a header drag. */}
          <div
            ref={viewportRef}
            style={{
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              onPointerDown={onHeaderPointerDown}
              onPointerMove={onHeaderPointerMove}
              onPointerUp={onHeaderPointerUp}
              onPointerCancel={onHeaderPointerCancel}
              style={{
                display: 'flex',
                height: '100%',
                width: `${((weekDayCount + 2) / weekDayCount) * 100}%`,
                transform: `translateX(calc(${-100 / (weekDayCount + 2)}% + ${dragDx}px))`,
                transition: dragging ? 'none' : 'transform 0.2s ease',
                cursor: dragging ? 'grabbing' : undefined,
              }}
            >
              {paddedDates.map((date) => {
                const dateStr = formatDate(date)
                return (
                  <DayColumn
                    key={dateStr}
                    date={date}
                    todos={getTodosForDate(dateStr)}
                    isToday={dateStr === today}
                  />
                )
              })}
            </div>
          </div>

          {/* Floating drag preview that follows the cursor.
              dropAnimation={null} prevents the overlay from snapping back to
              the source rect on release, eliminating the "ghost copy" flash. */}
          <DragOverlay dropAnimation={null}>
            {activeTodo && (
              <TodoItem todo={activeTodo} isOverlay />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Day-count selector footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '4px',
          padding: '12px',
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-paper)',
        }}
      >
        {DAY_COUNT_OPTIONS.map((n) => (
          <button
            key={n}
            onClick={() => setWeekDayCount(n)}
            style={{
              padding: '4px 10px',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.75rem',
              background: weekDayCount === n ? 'var(--color-paper-dark)' : 'transparent',
              color: weekDayCount === n ? 'var(--color-ink)' : 'var(--color-ink-muted)',
              border: '1px solid var(--color-border)',
              borderRadius: '3px',
              cursor: 'pointer',
            }}
          >
            {n}
          </button>
        ))}
      </div>

      <style>{`
        .week-nav-btn:hover { color: var(--color-ink) !important; }
      `}</style>
    </div>
  )
}
